import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { useTracking } from "@similarweb/tracking-provider";
import { Badge, Tooltip } from "@similarweb/ui-components";
import { Flex, Text, TruncatedText } from "@similarweb/ui-components/foundations";

import { CardLayout } from "../../common/card-layout/CardLayout";
import { OpenInNewTab } from "../../common/OpenInNewTab";
import type { NewsAlertItem } from "../hooks/useNewsAlerts";
import { getTopicTranslationKey } from "../utils/getTopicTranslationKey";

const HIGH_RELEVANCE_THRESHOLD = 66;

interface INewsAlertCardProps {
    alert: NewsAlertItem;
    favicon: string;
}

export const NewsAlertCard = ({ alert, favicon }: INewsAlertCardProps) => {
    const { track } = useTracking();
    const { translate } = useTranslate();
    const { headline, domain, content, formattedDate, newsType, topic, newsValueIndex, url, publisherDomain } = alert;

    const isHighRelevance = (newsValueIndex ?? 0) > HIGH_RELEVANCE_THRESHOLD;
    const isPR = newsType === "PR";

    const handleClick = () => {
        track("Alert Card", "click", "open alert/alerts/news");
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const rightSlot = isHighRelevance ? (
        <Tooltip content={translate("activation.alerts.cards.news.value-score.tooltip")}>
            <Badge
                type="number"
                data-automation="news-value-score-badge"
            >
                {translate("activation.alerts.cards.news.value-score.badge", { score: newsValueIndex })}
            </Badge>
        </Tooltip>
    ) : isPR ? (
        <Badge data-automation="news-press-release-badge">
            {translate("activation.alerts.cards.news.press-release.badge")}
        </Badge>
    ) : null;

    const leftSlot =
        isPR && topic === "News" ? null : (
            <Badge data-automation="news-topic-badge">{translate(getTopicTranslationKey(topic))}</Badge>
        );

    return (
        <CardLayout
            iconUrl={favicon}
            onClick={handleClick}
            data-automation="news-alert-card"
            data-domain={domain}
        >
            <CardLayout.Header rightSlot={rightSlot}>
                {leftSlot}
                <CardLayout.Title>{headline}</CardLayout.Title>
                <Text
                    flexShrink={0}
                    fontVariant="subtitle1"
                    color={colorsPalettes.carbon[250]}
                    data-automation="news-publisher-domain"
                >
                    {publisherDomain}
                </Text>
                <Text
                    flexShrink={0}
                    fontVariant="subtitle1"
                    color={colorsPalettes.carbon[250]}
                    data-automation="news-date"
                >
                    {formattedDate}
                </Text>
                <OpenInNewTab
                    url={url}
                    data-automation="news-external-link"
                />
            </CardLayout.Header>
            {content ? (
                <CardLayout.Content>
                    <Flex maxWidth="650px">
                        <TruncatedText
                            fontVariant="body2"
                            lineClamp={2}
                            data-automation="alert-description"
                        >
                            {content}
                        </TruncatedText>
                    </Flex>
                </CardLayout.Content>
            ) : null}
        </CardLayout>
    );
};